import { useFlag } from '@reflag/react-sdk';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import * as React from 'react';
import { useController, useFormContext } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import {
    SITE_VISITOR_DETERMINISTIC_JSON_SCHEMA_META_SCHEMA,
    SITE_VISITOR_JSON_SCHEMA_META_SCHEMA,
} from '@gitbook/app-adaptive-content';
import * as analytics from '@gitbook/app-analytics/client';
import { PlainCodeBlock } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    ButtonSkeleton,
    Code,
    confirm,
    Description,
    DialogRoot,
    DialogTrigger,
    Disclosure,
    DisclosurePanel,
    DisclosureTrigger,
    FieldGroup,
    Heading,
    PageSection,
    Skeleton,
    Tab,
    Tabs,
    TextInput,
    toast,
} from '@gitbook/spine-react';

import { useSetAnalyticsScreen } from '../../../navigation';
import { apiClient, apiQuery, getSitePermissions } from '../../../state';
import { DocumentationLink } from '../../Link';
import { FeatureFlagsIntegrationDialog } from '../FeatureFlagsIntegrationDialog';
import { JSONCodeEditor } from '../JSONCodeEditor';
import { useAdaptiveContentBuilderStateContext } from '../state';
import type { AdaptiveContentBuilderFormValues } from '../types';
import { FEATURE_FLAGS_PROVIDERS_SAMPLES } from '../utils';

/**
 * Section of the AdaptiveContentBuilderDialog to edit the adaptive schema (in JSON schema format) of a site.
 */
export function AdaptiveContentSiteSchemaEditor() {
    const adaptiveSchemaDynamicProps = useFlag('ADAPTIVE_CONTENT_DYNAMIC_PROPS');
    const { organizationId, siteId } = useAdaptiveContentBuilderStateContext();

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: siteId, organization: organizationId },
            {
                screen: 'adaptive_content_schema',
            }
        ),
        [siteId, organizationId]
    );

    return (
        <Disclosure defaultExpanded={true}>
            {({ isExpanded }) => (
                <div className="flex flex-1">
                    <div className="group/schema-editor flex flex-1 flex-col gap-2 px-10 py-6">
                        <div className="flex items-center gap-2">
                            <SetupVisitorSchemaStepHeading stepNumber={1} className="flex-1">
                                Create visitor schema
                            </SetupVisitorSchemaStepHeading>
                            <DisclosureTrigger>
                                <Button
                                    kind="blank"
                                    iconOnly
                                    leadingIcon={
                                        isExpanded ? Icon.PanelLeftOpen : Icon.PanelLeftClose
                                    }
                                    label={isExpanded ? 'Hide docs' : 'Show docs'}
                                    className="opacity-0 group-hover/schema-editor:opacity-100"
                                />
                            </DisclosureTrigger>
                        </div>
                        <Description>
                            The visitor schema defines what data you can pass to represent your
                            visitor, you can then use them in your expressions.
                        </Description>
                        <React.Suspense
                            fallback={
                                <ButtonSkeleton
                                    className="my-2"
                                    label="Import schema from integration"
                                />
                            }
                        >
                            <ImportFromFeatureFlagProviderButton
                                organizationId={organizationId}
                                siteId={siteId}
                            />
                        </React.Suspense>
                        <Description>
                            Edit the JSON schema below to define the visitor schema and use the code
                            samples on the right to start sending data.
                        </Description>
                        <SiteAdaptiveJSONSchemaField
                            hasAdaptiveSchemaDynamicProps={adaptiveSchemaDynamicProps.isEnabled}
                        />
                    </div>
                    <DisclosurePanel
                        orientation="horizontal"
                        className="flex h-full max-w-[50%] overflow-y-auto bg-side"
                    >
                        <div className="flex min-h-0 flex-col">
                            <div className="flex flex-col gap-2 px-10 pt-6 pb-4">
                                <SetupVisitorSchemaStepHeading stepNumber={2}>
                                    Pass data to your site
                                </SetupVisitorSchemaStepHeading>
                                <Description>
                                    There are different ways to pass visitor data to adapt your
                                    site's content.
                                </Description>
                            </div>
                            <SiteSchemaEditorPassDataDocsSection
                                organizationId={organizationId}
                                siteId={siteId}
                            />
                        </div>
                    </DisclosurePanel>
                </div>
            )}
        </Disclosure>
    );
}

function SiteAdaptiveJSONSchemaField(props: { hasAdaptiveSchemaDynamicProps: boolean }) {
    const { control, clearErrors, setError } = useFormContext<AdaptiveContentBuilderFormValues>();
    const { field } = useController({
        name: 'jsonSchema',
        control,
    });

    const onValidation = (errors: string[]) => {
        if (errors.length > 0) {
            setError('jsonSchema', { type: 'manual', message: 'Invalid schema' });
        } else {
            clearErrors();
        }
    };

    return (
        <FieldGroup className="h-full max-h-full overflow-auto">
            <React.Suspense
                fallback={
                    <Skeleton shape="rect" className="mt-4 h-[532px] w-full">
                        JSON Schema editor
                    </Skeleton>
                }
            >
                <JSONCodeEditor
                    value={field.value}
                    onChange={field.onChange}
                    onValidation={onValidation}
                    validationSettings={{
                        level: 'error',
                        jsonSchema: {
                            uri: 'inmemory://site-adaptive-schema.json',
                            fileMatch: ['*'],
                            schema: props.hasAdaptiveSchemaDynamicProps
                                ? SITE_VISITOR_JSON_SCHEMA_META_SCHEMA
                                : SITE_VISITOR_DETERMINISTIC_JSON_SCHEMA_META_SCHEMA,
                        },
                    }}
                />
            </React.Suspense>
        </FieldGroup>
    );
}

function SetupVisitorSchemaStepHeading(
    props: React.PropsWithChildren<{ stepNumber: number; className?: string }>
) {
    const { stepNumber, className } = props;
    return (
        <div className={clsx('flex items-center gap-2', className)}>
            <div
                className={
                    'flex size-6 shrink-0 items-center justify-center rounded-full border border-control text-ui-small tabular-nums transition'
                }
            >
                {stepNumber}
            </div>
            <Heading level={2}>{props.children}</Heading>
        </div>
    );
}

type AdaptiveContentPassDataDocsTab =
    | 'cookies'
    | 'feature-flags'
    | 'query-params'
    | 'visitor-authentication';

function SiteSchemaEditorPassDataDocsSection(props: {
    siteId: api.Site['id'];
    organizationId: string;
}) {
    const { siteId, organizationId } = props;
    const [selectedTab, setSelectedTab] = React.useState<AdaptiveContentPassDataDocsTab>('cookies');

    const scrollRef = React.useRef<HTMLDivElement>(null);

    const onChangeTab = (tab: AdaptiveContentPassDataDocsTab) => {
        setSelectedTab(tab);
        scrollRef.current?.scrollTo({ top: 0 });
    };

    return (
        <>
            <Tabs className="flex justify-around border-b px-10">
                <Tab
                    active={selectedTab === 'cookies'}
                    label="Cookies"
                    leadingIcon={Icon.Cookies}
                    onPress={() => onChangeTab('cookies')}
                />
                <Tab
                    active={selectedTab === 'feature-flags'}
                    label="Feature flags"
                    leadingIcon={Icon.FeatureFlags}
                    onPress={() => onChangeTab('feature-flags')}
                />
                <Tab
                    active={selectedTab === 'query-params'}
                    label="URL"
                    leadingIcon={Icon.Link}
                    onPress={() => onChangeTab('query-params')}
                />
                <Tab
                    active={selectedTab === 'visitor-authentication'}
                    label="Authenticated access"
                    leadingIcon={Icon.PublishStatusVisitorAuth}
                    onPress={() => onChangeTab('visitor-authentication')}
                />
            </Tabs>
            <div ref={scrollRef} className="overflow-y-auto px-10 py-6">
                {selectedTab === 'cookies' && (
                    <React.Suspense
                        fallback={
                            <Skeleton shape="rect" className="mt-4 h-[543px] w-full">
                                Docs Section
                            </Skeleton>
                        }
                    >
                        <SiteSchemaEditorPassDataViaCookiesDocs
                            organizationId={organizationId}
                            siteId={siteId}
                        />
                    </React.Suspense>
                )}
                {selectedTab === 'feature-flags' && <SiteSchemaEditorPassDataViaFeatureFlagsDocs />}
                {selectedTab === 'query-params' && <SiteSchemaEditorPassDataViaQueryParamsDocs />}
                {selectedTab === 'visitor-authentication' && (
                    <SiteSchemaEditorPassDataViaVisitorAuthDocs />
                )}
            </div>
        </>
    );
}

/**
 * Button to suggest the user to import feature flags from their provider.
 */
function ImportFromFeatureFlagProviderButton(props: {
    organizationId: api.Organization['id'];
    siteId: api.Site['id'];
}) {
    const { organizationId, siteId } = props;

    const [{ data: organization }, { data: siteStructure }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getSiteStructure', { organizationId, siteId }),
        ],
    });

    const canAdminIntegrationsOnOrg = organization ? organization.permissions.admin : false;
    const canCreateSiteContent = organization ? organization.permissions.createContent : false;
    const canAdminSite = siteStructure
        ? getSitePermissions({ siteStructure, organization }).canAdminSite
        : false;
    const canAdminIntegrationsOnSite =
        canAdminIntegrationsOnOrg || canAdminSite || canCreateSiteContent;

    if (!canAdminIntegrationsOnSite) {
        return null;
    }

    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    kind="secondary"
                    label="Import schema from integration"
                    className="my-2"
                    leadingIcon={Icon.Integrations}
                    trailingIcon={
                        <div className="ml-auto flex items-center gap-1">
                            <Icon.FeatureFlagsReflag />
                            <Icon.FeatureFlagsLaunchDarkly />
                            <Icon.ChevronRight />
                        </div>
                    }
                />
            </DialogTrigger>
            <FeatureFlagsIntegrationDialog organizationId={organizationId} siteId={siteId} />
        </DialogRoot>
    );
}

const PUBLIC_COOKIE_CODE_SAMPLE = `import Cookies from 'js-cookie';

const cookieData = {
  isLoggedIn: true,
  isBetaUser: false,
};

Cookies.set('gitbook-visitor-public', JSON.stringify(cookieData), {
  secure: true,
  domain: '*.acme.org',
})`;

const SIGNED_COOKIE_CODE_SAMPLE = `import { Request, Response } from 'express';
import { SignJWT } from 'jose';

const GITBOOK_VISITOR_SIGNING_KEY = Buffer.from(process.env.GITBOOK_VISITOR_SIGNING_KEY!, 'utf-8');
const GITBOOK_VISITOR_COOKIE_NAME = 'gitbook-visitor-token';

export async function handleAppLoginRequest(req: Request, res: Response) {
  const user = { id: '123', isBetaUser: true };
  const claims = { isBetaUser: user.isBetaUser };

  const token = await new SignJWT(claims)
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .setExpirationTime('2h')
    .sign(GITBOOK_VISITOR_SIGNING_KEY);

  res.cookie(GITBOOK_VISITOR_COOKIE_NAME, token, {
    httpOnly: true,
    secure: process.env.NODE_ENV === 'production',
    maxAge: 2 * 60 * 60 * 1000,
  });

  res.redirect('/');
}
`;

function SiteSchemaEditorPassDataViaCookiesDocs(props: {
    siteId: api.Site['id'];
    organizationId: string;
}) {
    const { siteId, organizationId } = props;
    const { data: sitePublishingAuth } = useSuspenseQuery(
        apiQuery('getSitePublishingAuthById', { organizationId, siteId: siteId })
    );
    return (
        <div className="flex flex-col gap-4">
            <PageSection>
                <p>Pass visitor data into your docs through a public or signed cookie.</p>
                <Alert
                    size="xsmall"
                    details={
                        <p>
                            This method only works if your site is served under a{' '}
                            <a
                                href="https://gitbook.com/docs/publishing-documentation/custom-domain"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                            >
                                custom domain
                            </a>
                            .
                        </p>
                    }
                />
            </PageSection>

            <PageSection>
                <Heading level={3}>Public cookie</Heading>
                <p>
                    To pass data to GitBook from a public cookie, you’ll need to send the data from
                    your application by setting <Code>gitbook-visitor-public</Code> cookie.
                </p>
                <p>
                    Data passed through public cookies must be defined in your visitor schema
                    through an{' '}
                    <DocumentationLink
                        path="publishing-documentation/adaptive-content/enabling-adaptive-content#setting-unsigned-claims"
                        host="docs"
                    >
                        unsigned
                    </DocumentationLink>{' '}
                    object.
                </p>
                <Heading level={4}>JS Example</Heading>
                <PlainCodeBlock
                    syntax="javascript"
                    code={PUBLIC_COOKIE_CODE_SAMPLE}
                    showToolbar={true}
                    className="[&&]:bg-base"
                />
            </PageSection>

            <PageSection>
                <Heading level={3}>Signed cookie</Heading>
                <p>
                    To pass data to GitBook more securely, you’ll need to send the data as a JSON
                    Web Token from your application in a cookie named{' '}
                    <Code>gitbook-visitor-token</Code> tied to your domain.
                </p>
                <p>
                    Sign the token with the key below in order to read claims when the user visits
                    your site.{' '}
                    <DocumentationLink
                        path="publishing-documentation/adaptive-content/enabling-adaptive-content/cookies#signed-cookie"
                        host="docs"
                    >
                        Learn more
                    </DocumentationLink>
                </p>
                <TextInput
                    size="medium"
                    readOnly
                    copyable
                    trailing={
                        <Button
                            label="Regenerate"
                            size="small"
                            kind="blank"
                            leadingIcon={Icon.Reset}
                            onPress={() => {
                                void confirm({
                                    title: 'Regenerate private key?',
                                    description:
                                        'Regenerating the private key will require you to update your authentication backend. Are you sure you want to continue?',
                                    kind: 'danger',
                                    confirmLabel: 'Regenerate',
                                    onConfirm: async (_event, { preventClose }) => {
                                        await toast.catch(
                                            async () => {
                                                await apiClient.sitePublishingAuth.regenerateSitePublishingAuthById(
                                                    organizationId,
                                                    siteId
                                                );
                                                toast.success('Private key regenerated');
                                            },
                                            'Error while regenerating private key',
                                            { onReject: preventClose }
                                        );
                                    },
                                });
                            }}
                        />
                    }
                    value={sitePublishingAuth.privateKey}
                />
                <Heading level={4}>TS Example</Heading>
                <PlainCodeBlock
                    syntax="typescript"
                    code={SIGNED_COOKIE_CODE_SAMPLE}
                    showToolbar={true}
                    className="[&&]:bg-base"
                />
            </PageSection>
        </div>
    );
}

function SiteSchemaEditorPassDataViaQueryParamsDocs() {
    return (
        <div className="flex flex-col gap-4">
            <PageSection>
                <p>Pass visitor data into your docs through URL query parameters.</p>
            </PageSection>
            <PageSection>
                <Heading level={3}>Query parameters</Heading>
                <p>
                    To pass data to GitBook through URL parameters, you’ll need to pass the data in
                    the URL in the format <Code>visitor.&lt;prop&gt;</Code>.
                </p>
                <p>
                    Data passed through query parameters must be defined in your visitor schema
                    through an{' '}
                    <DocumentationLink
                        path="publishing-documentation/adaptive-content/enabling-adaptive-content#setting-unsigned-claims"
                        host="docs"
                    >
                        unsigned
                    </DocumentationLink>{' '}
                    object.
                </p>
                <p>For example:</p>
                <PlainCodeBlock
                    syntax="bash"
                    code={'https://docs.acme.org/?visitor.language=fr&visitor.country=fr'}
                    showToolbar={true}
                    className="[&&]:bg-base"
                />
            </PageSection>

            <PageSection>
                <Alert
                    size="xsmall"
                    details={
                        <p>
                            Query parameters can be easily changed by the visitor and are best
                            suited for non-sensitive information.
                        </p>
                    }
                />
            </PageSection>
        </div>
    );
}

const AUTH_PROVIDERS_INFO = [
    {
        name: 'Azure AD',
        url: 'https://gitbook.com/docs/publishing-documentation/visitor-authentication/setting-up-azure-ad#id-3.-configure-azure-a-d-for-adaptive-content-optional',
    },
    {
        name: 'Auth0',
        url: 'https://gitbook.com/docs/publishing-documentation/visitor-authentication/setting-up-auth0#configure-auth0-for-adaptive-content-optional',
    },
    {
        name: 'AWS Cognito',
        url: 'https://gitbook.com/docs/publishing-documentation/visitor-authentication/setting-up-aws-cognito#id-3.-configure-aws-cognito-for-adaptive-content-optional',
    },
    {
        name: 'Okta',
        url: 'https://gitbook.com/docs/publishing-documentation/visitor-authentication/setting-up-okta#id-3.-configure-okta-for-adaptive-content-optional',
    },
    {
        name: 'OIDC',
        url: 'https://gitbook.com/docs/publishing-documentation/visitor-authentication/setting-up-oidc#id-3.-configure-oidc-for-adaptive-content-optional',
    },
] as const;

function SiteSchemaEditorPassDataViaVisitorAuthDocs() {
    return (
        <div className="flex flex-col gap-4">
            <PageSection>
                <p>
                    Pass visitor data into your docs through popular authentication providers such
                    as <strong>Auth0</strong>, <strong>Okta</strong>, <strong>AWS Cognito</strong>,
                    and more.
                </p>
            </PageSection>
            <PageSection>
                <Heading level={3}>Enrich JWT with custom claims</Heading>
                <p>
                    Each provider has a different method for enriching the JWT token with claims.
                    Read our guides on configuring your provider for adaptive content.
                </p>

                <ul className="list-disc pl-6 text-muted">
                    {AUTH_PROVIDERS_INFO.map((provider) => (
                        <li key={provider.name}>
                            <a
                                href={provider.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                            >
                                {provider.name}
                            </a>
                        </li>
                    ))}
                </ul>
            </PageSection>

            <PageSection>
                <Heading level={3}>Setting up a custom backend</Heading>
                <p>
                    For custom backends, ensure your server signs a JWT with the required claims and
                    shares a public key with GitBook.
                </p>
                <p>
                    Read more about custom backends in our{' '}
                    <DocumentationLink
                        path="publishing-documentation/authenticated-access/setting-up-a-custom-backend"
                        host="docs"
                    >
                        documentation.
                    </DocumentationLink>
                </p>
            </PageSection>
        </div>
    );
}

function SiteSchemaEditorPassDataViaFeatureFlagsDocs() {
    return (
        <div className="flex flex-col gap-4">
            <PageSection>
                <p>Pass visitor data into your docs through a feature flag provider.</p>
                <p>
                    GitBook provides helper functions and integrations for popular feature flag
                    service providers like <strong>LaunchDarkly</strong>.{' '}
                    <DocumentationLink
                        path="publishing-documentation/adaptive-content/enabling-adaptive-content/feature-flags"
                        host="docs"
                    >
                        Learn more
                    </DocumentationLink>
                </p>
            </PageSection>

            <PageSection>
                <Heading level={3}>LaunchDarkly</Heading>
                <p>
                    Use <Code>withLaunchDarkly</Code> with the LaunchDarkly React SDK to pass
                    context into GitBook.
                </p>
                <PlainCodeBlock
                    syntax="tsx"
                    code={FEATURE_FLAGS_PROVIDERS_SAMPLES.launchdarkly}
                    showToolbar={true}
                    className="[&&]:bg-base"
                />
                <p>
                    Once connected, any feature flag value available in LaunchDarkly will be exposed
                    as part of the visitor schema under the <Code>unsigned.launchdarkly.flags</Code>{' '}
                    object.
                </p>
            </PageSection>

            <PageSection>
                <Alert
                    size="xsmall"
                    details={
                        <p>
                            Feature flag values are evaluated on the client side, so avoid using
                            this method to pass sensitive or security-critical data.
                        </p>
                    }
                />
            </PageSection>
        </div>
    );
}
